import type { InputHTMLAttributes, ReactNode } from 'react';
import styles from './Switch.module.css';

/**
 * The Admin's on/off control (Warren, 21 October 2026: on/off settings are switches, 36 by 20, EVI green when on,
 * the theme's border grey when off, white knob, a visible focus ring, a 44 px tap area, the label beside it).
 *
 * It is a real checkbox with role="switch", so `checked`, `onChange`, `name` and form submission work exactly as
 * the checkbox it replaces did: nothing about what gets saved changes. `hideLabel` keeps the label for screen
 * readers only, for a switch in a table cell whose column and row already name it.
 */
type SwitchProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'role' | 'children'> & {
  label: ReactNode;
  hideLabel?: boolean;
  /** Extra class on the outer label, for layout only. */
  className?: string;
};

export function Switch({ label, hideLabel, className, ...input }: SwitchProps) {
  return (
    <label className={`${styles.root}${hideLabel ? ` ${styles.bare}` : ''}${className ? ` ${className}` : ''}`}>
      <span className={styles.control}>
        <input {...input} type="checkbox" role="switch" className={styles.input} />
        <span className={styles.track} aria-hidden><span className={styles.knob} /></span>
      </span>
      <span className={hideLabel ? styles.srOnly : styles.label}>{label}</span>
    </label>
  );
}
